import { compact, every, flow, isEmpty, isPlainObject, map, pick, some } from 'lodash/fp';

import getImageAttributes from '../../../../../../cms/utils/get-image-attributes';
import getLinkFromSanityTerm from '../../../../../../utils/get-link-from-sanity-term';
import type { ImageObject } from '../../../../../../types/__codegen__/sanity/graphql';
import type { PortableTextType } from '../../../../../../types/sanityInternal';
import type { Tile } from '../../../../../../components/content-tiles-section/types';

export type ItemType = {
  _id: string;
  __typename?: string;
  name: string;
  sloganRaw?: PortableTextType[];
  descriptionRaw?: PortableTextType[];
  imageObject?: ImageObject;
  summaryRaw?: PortableTextType[];
  detailsTeaser?: string;
};

type MappedContentTile = Omit<Tile, 'subtitle' | 'title'> &
  Required<Pick<Tile, 'subtitle' | 'title'>> &
  Pick<ItemType, '_id'>;

const mapContent = <T extends ItemType>(items: T[]): MappedContentTile[] =>
  flow(
    map((item: ItemType) => {
      if (!isPlainObject(item)) {
        return null;
      }

      const { _id, __typename, name, sloganRaw, descriptionRaw, imageObject, summaryRaw } = pick(
        ['_id', '__typename', 'name', 'sloganRaw', 'descriptionRaw', 'imageObject', 'summaryRaw'],
        item,
      );

      const mandatoryFieldsMissing =
        __typename === 'ExplainedTerm'
          ? some(isEmpty, [_id, sloganRaw, imageObject, summaryRaw])
          : some(isEmpty, [_id, descriptionRaw]) && every(isEmpty, [name, sloganRaw]);
      if (mandatoryFieldsMissing) {
        return null;
      }

      const image = getImageAttributes({ height: 240, imageObject });

      const ctaObject = compact([getLinkFromSanityTerm(item)]);

      return {
        _id,
        ctas: ctaObject,
        image,
        subtitle: __typename === 'ExplainedTerm' ? summaryRaw : descriptionRaw,
        title: isEmpty(sloganRaw) ? name : sloganRaw,
      };
    }),
    compact,
  )(items) as MappedContentTile[];

export default mapContent;
